import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Layout, { navCategories } from "@/components/Layout";
import OwnerDashboard from "@/pages/OwnerDashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import POSUpload from "@/pages/POSUpload";
import Incentives from "@/pages/Incentives";
import InventoryHub from "@/pages/InventoryHub";
import Quality from "@/pages/Quality";
import Config from "@/pages/Config";
import Attendance from "@/pages/Attendance";
import SalesAnalytics from "@/pages/SalesAnalytics";
import StockAudit from "@/pages/StockAudit";
import COGS from "@/pages/COGS";
import Procurement from "@/pages/Procurement";
import VendorMatrix from "@/pages/VendorMatrix";
import Banking from "@/pages/Banking";
import CashBook from "@/pages/CashBook";
import ProfitLoss from "@/pages/ProfitLoss";
import Analysis from "@/pages/Analysis";

import { Toaster } from "@/components/ui/sonner";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="h-screen flex items-center justify-center premium-bg text-slate-700 font-medium">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  return <Navigate to="/manager" replace />;
}

// Every guarded route takes its roles from the SAME table the sidebar renders
// from. Hand-writing `roles={["owner"]}` here would let the two drift, and the
// day they drift the menu is decoration rather than access control: a manager
// could no longer SEE Owner MIS but could still open it by typing the URL.
const routeRoles = Object.fromEntries(
  navCategories.flatMap((c) => c.items).map((i) => [i.to, i.roles])
);

// A path missing from the nav table has no declared audience, so it gets an
// empty one and everyone is bounced home — fail closed. Treating an unknown
// key as "no roles, let everybody in" would make the one mistake that matters
// the one that opens the door.
const guarded = (path, element) => (
  <Protected roles={routeRoles[path] || []}>{element}</Protected>
);

function App() {
  return (
    <div className="App min-h-screen premium-bg text-slate-900 font-sans selection:bg-slate-900 selection:text-white">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <Protected>
                  <HomeRedirect />
                </Protected>
              }
            />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/owner" element={guarded("/owner", <OwnerDashboard />)} />
              <Route path="/manager" element={guarded("/manager", <ManagerDashboard />)} />
              <Route path="/attendance" element={guarded("/attendance", <Attendance />)} />
              <Route path="/analytics" element={guarded("/analytics", <SalesAnalytics />)} />
              <Route path="/pos" element={guarded("/pos", <POSUpload />)} />
              <Route path="/incentives" element={guarded("/incentives", <Incentives />)} />
              <Route path="/inventory" element={guarded("/inventory", <InventoryHub />)} />
              <Route path="/audit" element={guarded("/audit", <StockAudit />)} />
              <Route path="/cogs" element={guarded("/cogs", <COGS />)} />
              <Route path="/procurement" element={guarded("/procurement", <Procurement />)} />
              <Route path="/vendors" element={guarded("/vendors", <VendorMatrix />)} />
              <Route path="/banking" element={guarded("/banking", <Banking />)} />
              <Route path="/cashbook" element={guarded("/cashbook", <CashBook />)} />
              <Route path="/pnl" element={guarded("/pnl", <ProfitLoss />)} />
              <Route path="/mis" element={guarded("/mis", <Analysis />)} />
              <Route path="/quality" element={guarded("/quality", <Quality />)} />
              <Route path="/config" element={guarded("/config", <Config />)} />
              {/* No catch-all meant any URL this router did not know rendered a
                  blank screen — so a removed page, a mistyped path or a stale
                  bookmark all produced an empty window. Sending it home is the
                  only alternative that never shows an empty page. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster theme="light" position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
